import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check as Tick } from "lucide-react";
import { Pill } from "@/components/site/Reveal";
import ShaderBackground from "@/components/site/ShaderBackground";
import Host from "@/components/site/Host";
import TalkFirst from "@/components/site/TalkFirst";
import { useOffers } from "@/components/site/Prices";
import { AUDIT, decode, result, summary } from "@/lib/check";
import { markFresh, remember } from "@/lib/room";
import { track } from "@/lib/track";

const FIELD =
  "h-12 w-full rounded-[14px] bg-white px-4 text-[16px] outline-none ring-1 ring-black/10 placeholder:text-black/30 focus:ring-black";
const LABEL = "text-[11px] uppercase tracking-[0.1em]";
// The whole way in, so the person booking sees where this step sits.
const PATH = ["Your details", "Agreement", "Payment", "Day 1"];
const day = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[14px] font-medium">{label}</span>
      {hint && <span className="ml-2 text-[13px] opacity-45">{hint}</span>}
      <span className="mt-2 block">{children}</span>
    </label>
  );
}

// Booking the Audit: the first of four steps, and the only one on this page.
// Five details open the client's own page with the agreement on it.
export default function Book() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const answers = decode(params.get("c"));
  const mine = answers ? result(answers) : null;
  const audit = useOffers().find((o) => o.key === "audit");
  const [status, setStatus] = useState(null);
  const [f, setF] = useState({
    company: "",
    address: "",
    name: "",
    email: "",
    email2: "",
    job: mine && mine.job !== "That job" ? mine.job : "",
    website: "",
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [wait, setWait] = useState({ email: "", state: "" });
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  useEffect(() => {
    track("book_opened", { from_check: Boolean(answers) });
    fetch("/api/book")
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus({ open: true, next: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setError("");
    try {
      const response = await fetch("/api/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...f,
          check: answers ? summary(answers) : "",
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "");
      if (!body.slug) throw new Error("");
      // This device is signed in to the new page from this moment.
      remember(body.slug);
      markFresh(body.slug, body.mailed ? "mailed" : body.code);
      track("book_submitted");
      navigate(`/a/${body.slug}`);
    } catch (err) {
      setError(
        err.message ||
          "That did not work. Try again in a moment, or write to hello@nomus.work.",
      );
      setState("idle");
    }
  };

  const full = status && !status.open;
  const same = f.email.trim().toLowerCase() === f.email2.trim().toLowerCase();
  // Only said once they have typed the second one in full.
  const differ =
    Boolean(f.email2) && f.email2.length >= f.email.length && !same;
  const ready =
    f.company && f.address && f.name && f.email && f.email2 && same && f.job;
  // Every place is taken: an address to be told at.
  const joinLine = async (e) => {
    e.preventDefault();
    if (wait.state === "busy" || !wait.email.trim()) return;
    setWait((w) => ({ ...w, state: "busy" }));
    try {
      const response = await fetch("/api/book/wait", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: wait.email.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "");
      setWait((w) => ({ ...w, state: "done" }));
    } catch (err) {
      setWait((w) => ({
        ...w,
        state: err.message || "That did not work. Try again in a moment.",
      }));
    }
  };
  const NEXT = [
    "Your own page opens, with the agreement filled in. You read it and sign it there.",
    `You pay ${AUDIT.price} by card. Nothing is charged before that.`,
    status?.walk
      ? "You pick a time for the walkthrough, from the next working day on. That day is day 1, and the two weeks run from it."
      : "By the end of the next working day we write with two times for the walkthrough. That day is day 1, and the two weeks run from it.",
  ];
  return (
    <div data-testid="book-page">
      <section className="relative bg-[#f7f7f7] px-5 pb-10 pt-24 sm:pb-14 sm:pt-28">
        <div className="absolute inset-0 overflow-hidden">
          <ShaderBackground strength={0.36} lines={11} fadeLeft={0.7} />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px]">
          <Pill white>Book the Audit</Pill>
          <ol
            className="mt-10 flex max-w-[640px] items-center gap-2 sm:mt-14 sm:gap-3"
            data-testid="book-path"
          >
            {PATH.map((name, i) => (
              <React.Fragment key={name}>
                {i > 0 && (
                  <li aria-hidden="true" className="h-px flex-1 bg-black/15" />
                )}
                <li
                  aria-current={i === 0 ? "step" : undefined}
                  className={`flex items-center gap-2 text-[13px] ${i ? "text-black/40" : "text-black"}`}
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] tabular-nums ${i ? "ring-1 ring-black/20" : "bg-black text-white"}`}
                  >
                    {i + 1}
                  </span>
                  <span className={i ? "hidden sm:inline" : ""}>{name}</span>
                </li>
              </React.Fragment>
            ))}
          </ol>
          <h1 className="nm-title mt-8 text-[44px] leading-[0.98] sm:text-[80px]">
            Start your Audit.
          </h1>
          <p className="mt-5 max-w-[600px] text-[19px] leading-[1.35] text-black/65 sm:text-[22px]">
            Five details open your own page, with the agreement on it. Nothing
            is charged at this step, and no clock starts until you pick the day.
          </p>
        </div>
      </section>

      <section className="px-3 pt-3 sm:px-5 sm:pt-5">
        <div className="mx-auto grid max-w-[1400px] gap-3 lg:grid-cols-12 lg:gap-5">
          <div className="lg:col-span-7">
            {full ? (
              <div
                className="rounded-[28px] bg-[#f7f7f7] p-7 sm:p-12"
                data-testid="book-full"
              >
                <p className={`${LABEL} opacity-45`}>No place free right now</p>
                <p className="nm-title mt-3 text-[30px] leading-[1.05] sm:text-[44px]">
                  Every place is taken until {day(status.next)}.
                </p>
                <p className="mt-4 max-w-[520px] text-[17px] leading-[1.45] opacity-65">
                  We run a small number of Audits at a time, so each one gets
                  our full attention. Leave your address and we write once, the
                  day a place opens. Or talk to us now.
                </p>
                {wait.state === "done" ? (
                  <p
                    className="mt-6 flex items-start gap-2 text-[16px] leading-[1.45]"
                    data-testid="book-waiting"
                  >
                    <Tick size={15} className="mt-[4px] shrink-0" />
                    Noted. You get one email from us, the day a place opens.
                  </p>
                ) : (
                  <form
                    onSubmit={joinLine}
                    className="mt-6 flex max-w-[520px] flex-col gap-2 sm:flex-row"
                    data-testid="book-wait"
                  >
                    <input
                      aria-label="Your work email"
                      placeholder="you@company.com"
                      type="email"
                      autoComplete="email"
                      value={wait.email}
                      onChange={(e) =>
                        setWait({ email: e.target.value, state: "" })
                      }
                      className={`${FIELD} sm:flex-1`}
                    />
                    <button
                      type="submit"
                      disabled={wait.state === "busy" || !wait.email.trim()}
                      className="h-12 shrink-0 rounded-full bg-black px-6 text-[13px] font-medium text-white transition-colors hover:bg-[#262626] disabled:opacity-40"
                    >
                      {wait.state === "busy" ? "Saving…" : "Tell me when"}
                    </button>
                  </form>
                )}
                {wait.state && !["busy", "done"].includes(wait.state) && (
                  <p role="alert" className="mt-3 text-[14px] text-red-600">
                    {wait.state}
                  </p>
                )}
                <div className="mt-6">
                  <TalkFirst
                    location="book_full"
                    answers={answers}
                    tone="light"
                  />
                </div>
              </div>
            ) : (
              <form
                onSubmit={send}
                noValidate
                className="rounded-[28px] bg-[#f7f7f7] p-7 sm:p-12"
                data-testid="book-form"
              >
                <fieldset>
                  <legend className={`${LABEL} opacity-45`}>Your firm</legend>
                  <div className="mt-4 space-y-4">
                    <Field label="Legal name">
                      <input
                        autoComplete="organization"
                        value={f.company}
                        onChange={set("company")}
                        className={FIELD}
                      />
                    </Field>
                    <Field
                      label="Address"
                      hint="As it should read on the agreement"
                    >
                      <input
                        autoComplete="street-address"
                        value={f.address}
                        onChange={set("address")}
                        className={FIELD}
                      />
                    </Field>
                  </div>
                </fieldset>

                <fieldset className="mt-9 border-t border-black/10 pt-8">
                  <legend className="sr-only">You</legend>
                  <p className={`${LABEL} opacity-45`}>You</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Field label="Full name">
                        <input
                          autoComplete="name"
                          value={f.name}
                          onChange={set("name")}
                          className={FIELD}
                        />
                      </Field>
                    </div>
                    <Field label="Work email">
                      <input
                        type="email"
                        autoComplete="email"
                        value={f.email}
                        onChange={set("email")}
                        className={FIELD}
                      />
                    </Field>
                    <Field label="Work email, once more">
                      <input
                        type="email"
                        autoComplete="email"
                        value={f.email2}
                        onChange={set("email2")}
                        aria-invalid={differ}
                        className={`${FIELD} ${differ ? "ring-red-500" : ""}`}
                      />
                    </Field>
                  </div>
                  {differ && (
                    <p
                      role="alert"
                      className="mt-3 text-[13px] text-red-600"
                      data-testid="book-differ"
                    >
                      The two addresses are not the same.
                    </p>
                  )}
                  <p className="mt-3 text-[13px] leading-[1.45] opacity-50">
                    Your sign-in link goes to this address, which is why we ask
                    for it twice. If someone else signs for the firm, you can
                    send the page to them from the next step.
                  </p>
                </fieldset>

                <fieldset className="mt-9 border-t border-black/10 pt-8">
                  <legend className="sr-only">The job</legend>
                  <p className={`${LABEL} opacity-45`}>The job</p>
                  <div className="mt-4">
                    <Field
                      label="The one job this Audit studies"
                      hint="One line"
                    >
                      <input
                        placeholder="For example: month-end close"
                        value={f.job}
                        onChange={set("job")}
                        className={FIELD}
                      />
                    </Field>
                  </div>
                  {mine && (
                    <p
                      className="mt-3 flex items-start gap-2 text-[13px] leading-[1.45] opacity-60"
                      data-testid="book-mine"
                    >
                      <Tick size={13} className="mt-[3px] shrink-0" />
                      <span>
                        From your check: {mine.hours[0]}–{mine.hours[1]} hours a
                        month, stalling at {mine.leak.toLowerCase()}. It goes
                        onto your page.
                      </span>
                    </p>
                  )}
                </fieldset>

                <div
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
                >
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={f.website}
                      onChange={set("website")}
                    />
                  </label>
                </div>

                <div className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-black/10 pt-8">
                  <button
                    type="submit"
                    disabled={state === "sending" || !ready}
                    data-testid="book-submit"
                    className="h-12 rounded-full bg-black px-8 text-[14px] font-medium text-white transition-colors hover:bg-[#262626] disabled:opacity-40"
                  >
                    {state === "sending" ? "Opening…" : "Open my Audit page"}
                  </button>
                  <span className="text-[13px] opacity-50">
                    Nothing is charged at this step.
                  </span>
                </div>
                {error && (
                  <p
                    role="alert"
                    className="mt-4 text-[14px] text-red-600"
                    data-testid="book-error"
                  >
                    {error}
                  </p>
                )}
              </form>
            )}
            {!full && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-4 rounded-[28px] bg-[#f7f7f7] px-7 py-6 sm:px-12 lg:mt-5">
                <p className="text-[16px] leading-[1.4]">
                  Would you sooner talk to a person first?
                </p>
                <TalkFirst
                  location="book_page"
                  answers={answers}
                  tone="light"
                />
              </div>
            )}
          </div>

          <aside className="self-start rounded-[28px] bg-black p-7 text-white sm:p-10 lg:sticky lg:top-24 lg:col-span-5">
            <p className={`${LABEL} text-white/45`}>What you are booking</p>
            <div className="mt-3 flex items-baseline justify-between gap-4">
              <p className="nm-title text-[30px] leading-none sm:text-[36px]">
                The Audit
              </p>
              <p className="nm-display text-[40px] leading-none tracking-[-0.03em] sm:text-[52px]">
                {AUDIT.price}
              </p>
            </div>
            <p className="mt-3 text-[15px] leading-[1.45] text-white/55">
              Paid once. Two weeks, counted from the day of your walkthrough.
              About three hours of your team&rsquo;s time.
            </p>

            {audit && (
              <>
                <p className={`${LABEL} mt-8 text-white/45`}>
                  You hold at the end
                </p>
                <ul className="mt-3 space-y-2.5">
                  {audit.holds.map((line) => (
                    <li
                      key={line}
                      className="flex items-start gap-3 text-[16px] leading-[1.4]"
                    >
                      <Tick
                        size={14}
                        className="mt-[4px] shrink-0 text-white/60"
                      />
                      {line}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p className={`${LABEL} mt-8 text-white/45`}>What happens next</p>
            <ol className="mt-3">
              {NEXT.map((line, i) => (
                <li
                  key={line}
                  className="grid grid-cols-[2rem_1fr] border-t border-white/15 py-3.5 text-[15px] leading-[1.45] text-white/80 first:border-0 first:pt-0"
                >
                  <span className="pt-[3px] text-[12px] tabular-nums text-white/40">
                    0{i + 2}
                  </span>
                  {line}
                </li>
              ))}
            </ol>

            <p className={`${LABEL} mt-8 text-white/45`}>If it goes wrong</p>
            <ul className="mt-3 space-y-2.5 text-[15px] leading-[1.45] text-white/80">
              <li>
                If we read what you tell us and think the Audit is wrong for
                you, we say so by the end of the next working day and refund the
                fee in full.
              </li>
              <li>
                If the written Audit is not with you by day 14 for a reason on
                our side, we refund the fee in full.
              </li>
            </ul>

            <div className="mt-8 border-t border-white/15 pt-6">
              <Host dark line="Runs your Audit. Ask anything:" />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
